// De indeling van een Kavel (`charging_locations`) — T2.5.
//
// Twee vragen, één bestand, omdat elk scherm ze allebei stelt:
//
//   1. WAAR staat deze Kavel in het traject?  → fase (4) en stadium (11).
//   2. DOET er hier nog iets?                 → actief, slapend of gearchiveerd.
//
// Vóór T2.5 stond de stadium→fase-indeling hier met vier regels en een
// `return 'acquisitie'` als vangnet. Dat vangnet was het probleem: een stadium
// dat niemand kende — een typefout, een waarde uit een oude import, een
// stadium dat een volgende migratie toevoegt — werd stil als acquisitie geteld
// en verscheen in de acquisitie-telling zonder dat iemand het merkte. De
// database heeft géén CHECK op `stage` (gemeten 12-09-2026), dus die waarde
// kan er daadwerkelijk in staan.
//
// Nu valt onbekend in een zichtbare restgroep (`onbekend`, "Niet ingedeeld").
// Vandaag is die groep leeg — alle 34 Kavels dragen een van de acht gebruikte
// stadia — en dat is precies de bedoeling: een groep die vult als er iets
// misgaat, in plaats van een telling die stil scheef staat.
//
// De labels stonden vóór T2.5 op vijf plekken los van elkaar
// (ChargingAcquisitie, ChargingRealisatie, DashboardChargingOverview,
// LocationModal, ChargingLocationDetail.getStageInfo). Die laatste kende er
// zeven van de elf, dus een Kavel in 'nieuw' kreeg een grijze badge met de
// ruwe waarde. Eén tabel hier, en dat kan niet meer.

/** De vier fasen. De volgorde is de volgorde van het traject. */
export type KavelFase = 'acquisitie' | 'realisatie' | 'operatie' | 'verloren';

/** Een fase, of de restgroep voor een stadium dat we niet kennen. */
export type KavelFaseOfRest = KavelFase | 'onbekend';

export interface FaseInfo {
  id: KavelFaseOfRest;
  label: string;
  /** Hex, zoals de Gauw-tabel en het dashboard hem al gebruiken. */
  color: string;
  /** Tailwind-variant, zoals de kanban-koppen hem gebruiken. */
  dot: string;
}

export const KAVEL_FASEN: readonly FaseInfo[] = [
  { id: 'acquisitie', label: 'Acquisitie', color: '#0091ff', dot: 'bg-blue-500' },
  { id: 'realisatie', label: 'Realisatie', color: '#ff9100', dot: 'bg-orange-500' },
  { id: 'operatie', label: 'Operatie', color: '#28a745', dot: 'bg-green-500' },
  { id: 'verloren', label: 'Verloren', color: '#e53935', dot: 'bg-red-500' },
];

/**
 * De restgroep. Geen vijfde fase — een vergaarbak die hoort leeg te zijn, en
 * die je ziet zodra hij dat niet is.
 */
export const FASE_ONBEKEND: FaseInfo = {
  id: 'onbekend',
  label: 'Niet ingedeeld',
  color: '#6b7280',
  dot: 'bg-gray-500',
};

/** De vier fasen plus de restgroep — voor elk scherm dat álles moet tonen. */
export const KAVEL_FASEN_MET_REST: readonly FaseInfo[] = [...KAVEL_FASEN, FASE_ONBEKEND];

/**
 * De elf stadia, per fase, in trajectvolgorde.
 *
 * Gemeten op fxlorumfwzrdwiftbalo (12-09-2026): acht van de elf zijn in
 * gebruik. `definitief_ontwerp`, `bouwvoorbereiding` en `bouwuitvoering`
 * hebben nul Kavels maar staan in de kanban van ChargingRealisatie en in de
 * labels van het dashboard — ze blijven dus staan.
 */
export const STADIA_PER_FASE: Record<KavelFase, readonly string[]> = {
  acquisitie: ['nieuw', 'evaluatie', 'uitwerking', 'onderhandeling', 'contracting'],
  realisatie: ['voorlopig_ontwerp', 'definitief_ontwerp', 'bouwvoorbereiding', 'bouwuitvoering'],
  operatie: ['operatie'],
  verloren: ['verloren'],
};

/** Alle elf stadia, in trajectvolgorde. */
export const KAVEL_STADIA: readonly string[] = KAVEL_FASEN.flatMap(
  (f) => STADIA_PER_FASE[f.id as KavelFase],
);

/** Hoe een stadium op het scherm heet. Eén tabel voor het hele CRM. */
export const STADIUM_LABELS: Record<string, string> = {
  nieuw: 'Nieuw',
  evaluatie: 'Evaluatie',
  uitwerking: 'Uitwerking',
  onderhandeling: 'Onderhandeling',
  contracting: 'Contracting',
  voorlopig_ontwerp: 'Voorlopig ontwerp',
  definitief_ontwerp: 'Definitief ontwerp',
  bouwvoorbereiding: 'Bouwvoorbereiding',
  bouwuitvoering: 'Bouwuitvoering',
  operatie: 'Operatie',
  verloren: 'Verloren',
};

/** Korte varianten voor smalle kolommen (dashboard-strip, kanban-koppen). */
export const STADIUM_LABELS_KORT: Record<string, string> = {
  ...STADIUM_LABELS,
  onderhandeling: 'Onderh.',
  contracting: 'Contract',
  voorlopig_ontwerp: 'VO',
  definitief_ontwerp: 'DO',
  bouwvoorbereiding: 'Bouwvoorb.',
  bouwuitvoering: 'Bouw',
};

const FASE_VAN_STADIUM: Record<string, KavelFase> = (() => {
  const index: Record<string, KavelFase> = {};
  for (const fase of KAVEL_FASEN) {
    for (const stadium of STADIA_PER_FASE[fase.id as KavelFase]) {
      index[stadium] = fase.id as KavelFase;
    }
  }
  return index;
})();

/**
 * In welke fase valt dit stadium?
 *
 * Geen vangnet: wat we niet kennen heet `onbekend` en is daarmee te zien.
 * `null`, `undefined` en een lege string vallen er ook in — een Kavel zonder
 * stadium bestaat in de database niet (`stage` is NOT NULL met default
 * 'prospect'), maar een rij uit een `select` met een smalle kolomlijst kan het
 * veld missen, en dan liegt een default-fase.
 */
export function faseVanStadium(stadium: string | null | undefined): KavelFaseOfRest {
  if (!stadium) return 'onbekend';
  return FASE_VAN_STADIUM[stadium.toLowerCase()] ?? 'onbekend';
}

/** De stadia van één fase; voor de restgroep een lege lijst. */
export function stadiaVanFase(fase: KavelFaseOfRest): readonly string[] {
  return STADIA_PER_FASE[fase as KavelFase] ?? [];
}

/** Het schermlabel van een fase, restgroep inbegrepen. */
export function faseLabel(fase: KavelFaseOfRest): string {
  return KAVEL_FASEN_MET_REST.find((f) => f.id === fase)?.label ?? FASE_ONBEKEND.label;
}

/** De hele fase-info, restgroep inbegrepen. */
export function faseInfo(fase: KavelFaseOfRest): FaseInfo {
  return KAVEL_FASEN_MET_REST.find((f) => f.id === fase) ?? FASE_ONBEKEND;
}

/**
 * Het schermlabel van een stadium. Een stadium dat we niet kennen wordt niet
 * verzwegen maar leesbaar gemaakt: `iets_nieuws` → "Iets nieuws".
 */
export function stadiumLabel(stadium: string | null | undefined): string {
  if (!stadium) return 'Onbekend';
  return (
    STADIUM_LABELS[stadium.toLowerCase()] ??
    stadium.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
  );
}

/* ── Doet er hier nog iets? ────────────────────────────────────────────── */

/**
 * De drie standen van een Kavel. Ze sluiten elkaar uit; migratie 084 legt dat
 * vast met `num_nonnulls(deleted_at, dormant_at) <= 1`.
 *
 *   * `actief`        — staat in de pijplijn en telt mee.
 *   * `slapend`       — hier gebeurt nu niets. Standaard verborgen, met één
 *                       klik terug. Géén archief: het dossier is niet weg, het
 *                       ligt stil.
 *   * `gearchiveerd`  — uit de portefeuille. Leeft op /charging/archief.
 */
export type KavelZicht = 'actief' | 'slapend' | 'gearchiveerd';

export interface KavelZichtVelden {
  deleted_at?: string | null;
  dormant_at?: string | null;
}

/**
 * Welke stand heeft deze Kavel?
 *
 * Archief wint van slapend als beide gevuld staan. Dat kan de database sinds
 * 084 niet meer, maar deze functie draait ook op rijen van vóór die migratie
 * en op een `select` die `dormant_at` niet meevraagt — en dan is "onzichtbaar
 * in twee standen tegelijk" een slechtere uitkomst dan één duidelijke.
 */
export function kavelZicht(rij: KavelZichtVelden | null | undefined): KavelZicht {
  if (!rij) return 'actief';
  if (rij.deleted_at) return 'gearchiveerd';
  if (rij.dormant_at) return 'slapend';
  return 'actief';
}

export interface KavelTelling {
  /** Alles wat de query opleverde. */
  totaal: number;
  actief: number;
  slapend: number;
  gearchiveerd: number;
  /** Wat er met de huidige schakelaarstand op het scherm staat. */
  zichtbaar: number;
  /** Wat er door de schakelaar buiten beeld valt — nooit stil. */
  verborgen: number;
}

/**
 * Tel een set Kavels uit. `zichtbaar + verborgen === totaal`, altijd: dat is
 * de eis "niets verdwijnt stil" als rekensom.
 */
export function telKavels(
  rijen: readonly KavelZichtVelden[] | null | undefined,
  opties: { toonSlapend?: boolean } = {},
): KavelTelling {
  const lijst = rijen ?? [];
  let actief = 0;
  let slapend = 0;
  let gearchiveerd = 0;
  for (const rij of lijst) {
    const zicht = kavelZicht(rij);
    if (zicht === 'actief') actief++;
    else if (zicht === 'slapend') slapend++;
    else gearchiveerd++;
  }
  const zichtbaar = actief + (opties.toonSlapend ? slapend : 0);
  return {
    totaal: lijst.length,
    actief,
    slapend,
    gearchiveerd,
    zichtbaar,
    verborgen: lijst.length - zichtbaar,
  };
}

/**
 * De standaardlijst: actieve Kavels, en slapende alleen met de schakelaar aan.
 * Gearchiveerde horen hier nooit bij — die hebben hun eigen scherm.
 */
export function filterKavels<T extends KavelZichtVelden>(
  rijen: readonly T[] | null | undefined,
  opties: { toonSlapend?: boolean } = {},
): T[] {
  return (rijen ?? []).filter((rij) => {
    const zicht = kavelZicht(rij);
    if (zicht === 'gearchiveerd') return false;
    if (zicht === 'slapend') return opties.toonSlapend === true;
    return true;
  });
}

/* ── Groeperen: fase-telling en de partities buiten de pijplijn ────────── */

export interface FaseTelling {
  fase: KavelFaseOfRest;
  /** Wakker en niet gearchiveerd. Dit is het getal op de fase-strip. */
  actief: number;
  /** Slapend. Staat er apart bij, want het telt niet mee als actief. */
  slapend: number;
}

export interface FaseTellingen {
  /** Alle vijf de groepen, altijd, ook als ze leeg zijn. */
  perFase: FaseTelling[];
  /** Gearchiveerd valt buiten de fase-telling, maar niet buiten de som. */
  gearchiveerd: number;
  totaal: number;
}

/**
 * Tel Kavels per fase, met slapend apart.
 *
 * Stond tot de scepticus-ronde als `useMemo` in `KavelFaseStrip.tsx`, en was
 * daardoor door geen enkele test te raken: een mutatie die slapend als actief
 * telde bleef groen. Hier is hij toetsbaar, en de invariant
 * `Σ(actief + slapend) + gearchiveerd === totaal` is de bewaking dat er niets
 * tussenuit valt.
 */
export function telFasen<T extends KavelZichtVelden & { stage?: string | null }>(
  kavels: readonly T[] | null | undefined,
): FaseTellingen {
  const lijst = kavels ?? [];
  const perFase = KAVEL_FASEN_MET_REST.map<FaseTelling>((f) => ({
    fase: f.id,
    actief: 0,
    slapend: 0,
  }));
  const index = new Map(perFase.map((t) => [t.fase, t]));

  let gearchiveerd = 0;
  for (const kavel of lijst) {
    const zicht = kavelZicht(kavel);
    if (zicht === 'gearchiveerd') {
      gearchiveerd++;
      continue;
    }
    // `faseVanStadium` geeft altijd een van de vijf, dus de lookup kan niet
    // missen — en een Kavel kan hier niet uit de telling vallen.
    const rij = index.get(faseVanStadium(kavel.stage))!;
    if (zicht === 'slapend') rij.slapend++;
    else rij.actief++;
  }

  return { perFase, gearchiveerd, totaal: lijst.length };
}

export interface BuitenPijplijn<T> {
  gearchiveerd: T[];
  slapend: T[];
  verloren: T[];
  nietIngedeeld: T[];
  /** Alles wat wél op een fase-scherm staat. Hier niet getoond, wel geteld. */
  inPijplijn: T[];
}

/**
 * Verdeel Kavels over de groepen van het Archief-scherm.
 *
 * Elke Kavel valt in precies één partitie, en de volgorde van de vragen ís de
 * voorrang: gearchiveerd → slapend → verloren → niet ingedeeld → pijplijn.
 * Een slapende Kavel die óók verloren is staat dus bij slapend: zijn stand is
 * specifieker dan zijn fase, en twee keer tonen maakt elke telling onbetrouwbaar.
 *
 * Stond tot de scepticus-ronde als vier losse `useMemo`'s in
 * `ChargingArchief.tsx`. Een mutatie die de slapend-partitie leeg liet bleef
 * groen — terwijl dat letterlijk het gebrek is dat T2.5 dicht.
 */
export function partitioneerBuitenPijplijn<T extends KavelZichtVelden & { stage?: string | null }>(
  kavels: readonly T[] | null | undefined,
): BuitenPijplijn<T> {
  const uit: BuitenPijplijn<T> = {
    gearchiveerd: [],
    slapend: [],
    verloren: [],
    nietIngedeeld: [],
    inPijplijn: [],
  };

  for (const kavel of kavels ?? []) {
    const zicht = kavelZicht(kavel);
    if (zicht === 'gearchiveerd') {
      uit.gearchiveerd.push(kavel);
      continue;
    }
    if (zicht === 'slapend') {
      uit.slapend.push(kavel);
      continue;
    }
    const fase = faseVanStadium(kavel.stage);
    if (fase === 'verloren') uit.verloren.push(kavel);
    else if (fase === 'onbekend') uit.nietIngedeeld.push(kavel);
    else uit.inPijplijn.push(kavel);
  }

  return uit;
}

/* ── Wat er van de oude vorm overblijft ────────────────────────────────── */

/**
 * Zelfde vorm als vóór T2.5, zodat bestaande aanroepen blijven werken.
 * Afgeleid van `STADIA_PER_FASE`, zodat er maar één lijst is.
 */
export const STAGE_GROUPS: Record<string, string[]> = Object.fromEntries(
  KAVEL_FASEN.map((f) => [f.id, [...STADIA_PER_FASE[f.id as KavelFase]]]),
);

/**
 * @deprecated Gebruik `faseVanStadium`. Deze naam blijft omdat drie schermen
 * hem aanroepen; het gedrag is wél veranderd — onbekend geeft nu `'onbekend'`
 * in plaats van stil `'acquisitie'`.
 */
export const getStageGroup = (stage: string): KavelFaseOfRest => faseVanStadium(stage);

/** @deprecated Gebruik `stadiaVanFase`. */
export const getAllStagesInGroup = (group: string): string[] => [
  ...stadiaVanFase(group as KavelFaseOfRest),
];
